'use client';

/**
 * Environment Variables Tab
 *
 * Reusable component for managing environment variables at org or project scope.
 * Shows a key-value editor with masked values (write-only after creation).
 */

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  Center,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Inline,
  Input,
  Label,
  Spinner,
  Stack,
  Text,
} from '@cortex/ui';
import { Eye, EyeOff, Key, Plus, Trash2, Upload } from 'lucide-react';
import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useApi } from '@/hooks/use-api';

interface EnvVarMetadata {
  id: string;
  organizationId: string;
  projectId: string | null;
  key: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

function parseEnvFile(content: string): Array<{ key: string; value: string }> {
  const results: Array<{ key: string; value: string }> = [];

  for (const rawLine of content.split(/\r?\n/)) {
    const line = rawLine.trim();

    if (!line || line.startsWith('#')) continue;

    const stripped = line.startsWith('export ') ? line.slice(7).trim() : line;
    const eqIndex = stripped.indexOf('=');
    if (eqIndex === -1) continue;

    const key = stripped.slice(0, eqIndex).trim();
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(key)) continue;

    let value = stripped.slice(eqIndex + 1).trim();

    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1);
    }

    results.push({ key, value });
  }

  return results;
}

interface EnvironmentVariablesTabProps {
  organizationId: string;
  projectId?: string;
}

export function EnvironmentVariablesTab({
  organizationId,
  projectId,
}: EnvironmentVariablesTabProps) {
  const getApi = useApi();
  const [vars, setVars] = useState<EnvVarMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add/Edit state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingKey, setEditingKey] = useState('');
  const [editingValue, setEditingValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Delete state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingVar, setDeletingVar] = useState<EnvVarMetadata | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Import state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({
    current: 0,
    total: 0,
  });
  const [importError, setImportError] = useState<string | null>(null);

  // Value visibility
  const [showValue, setShowValue] = useState(false);

  // Fetch variables
  const fetchVars = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const api = await getApi();
      const query: Record<string, string> = { organizationId };
      if (projectId) {
        query.projectId = projectId;
      }

      const response = await api.environmentVariables.index.$get({
        query,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch environment variables');
      }

      const json = await response.json();
      if ('data' in json) {
        setVars(json.data as unknown as EnvVarMetadata[]);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to load environment variables'
      );
    } finally {
      setLoading(false);
    }
  }, [getApi, organizationId, projectId]);

  useEffect(() => {
    fetchVars();
  }, [fetchVars]);

  // Save variable
  const handleSave = async () => {
    if (!editingKey.trim() || !editingValue.trim()) return;

    setSaving(true);
    setSaveError(null);

    try {
      const api = await getApi();
      const response = await api.environmentVariables.index.$post({
        json: {
          organizationId,
          projectId: projectId ?? null,
          key: editingKey.trim(),
          value: editingValue,
        },
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(
          ('error' in errData ? errData.error : null) ||
            'Failed to save environment variable'
        );
      }

      setAddModalOpen(false);
      setEditingKey('');
      setEditingValue('');
      setShowValue(false);
      await fetchVars();
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'Failed to save variable'
      );
    } finally {
      setSaving(false);
    }
  };

  // Delete variable
  const handleDelete = async () => {
    if (!deletingVar) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      const api = await getApi();
      const response = await api.environmentVariables[':id'].$delete({
        param: { id: deletingVar.id },
      });

      if (!response.ok) {
        throw new Error('Failed to delete environment variable');
      }

      setDeleteModalOpen(false);
      setDeletingVar(null);
      await fetchVars();
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : 'Failed to delete variable'
      );
    } finally {
      setDeleting(false);
    }
  };

  // Open update modal
  const handleUpdate = (envVar: EnvVarMetadata) => {
    setEditingKey(envVar.key);
    setEditingValue('');
    setSaveError(null);
    setShowValue(false);
    setAddModalOpen(true);
  };

  // Import .env file
  const handleImport = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    event.target.value = '';

    setImporting(true);
    setImportError(null);
    setImportProgress({ current: 0, total: 0 });

    try {
      const content = await file.text();
      const parsed = parseEnvFile(content);

      if (parsed.length === 0) {
        setImportError('No valid variables found in the file.');
        setImporting(false);
        return;
      }

      setImportProgress({ current: 0, total: parsed.length });
      const failures: string[] = [];
      const api = await getApi();

      for (let i = 0; i < parsed.length; i++) {
        const entry = parsed[i]!;
        try {
          const response = await api.environmentVariables.index.$post({
            json: {
              organizationId,
              projectId: projectId ?? null,
              key: entry.key,
              value: entry.value,
            },
          });

          if (!response.ok) {
            failures.push(entry.key);
          }
        } catch {
          failures.push(entry.key);
        }
        setImportProgress({ current: i + 1, total: parsed.length });
      }

      await fetchVars();

      if (failures.length > 0) {
        setImportError(
          `Failed to import ${failures.length} variable(s): ${failures.join(', ')}`
        );
      }
    } catch (err) {
      setImportError(
        err instanceof Error ? err.message : 'Failed to read file'
      );
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <Center minHeight="12rem">
        <Spinner size="md" variant="muted" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center minHeight="12rem">
        <Stack gap="sm" align="center">
          <Text color="error">{error}</Text>
          <Button variant="outline" size="sm" onClick={fetchVars}>
            Retry
          </Button>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="md">
      {/* Header */}
      <Inline justify="between">
        <Stack gap="none">
          <Text size="lg" className="font-medium">
            Environment Variables
          </Text>
          <Text color="muted" size="sm">
            {projectId
              ? 'Project-level variables override organization-level variables with the same key.'
              : 'Organization-level variables are shared across all projects.'}
          </Text>
        </Stack>
        <Inline gap="xs">
          <input
            ref={fileInputRef}
            type="file"
            accept=".env,.env.*,.txt"
            className="hidden"
            onChange={handleImport}
          />
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
          >
            <Upload className="h-4 w-4" />
            Import .env
          </Button>
          <Button
            size="sm"
            className="gap-2"
            onClick={() => {
              setEditingKey('');
              setEditingValue('');
              setSaveError(null);
              setShowValue(false);
              setAddModalOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Add Variable
          </Button>
        </Inline>
      </Inline>

      {/* Import progress / error */}
      {importing && (
        <Alert>
          <AlertDescription className="flex items-center gap-2">
            <Spinner size="sm" />
            Importing {importProgress.current} of {importProgress.total}{' '}
            variables...
          </AlertDescription>
        </Alert>
      )}
      {importError && !importing && (
        <Alert variant="destructive">
          <AlertDescription>{importError}</AlertDescription>
        </Alert>
      )}

      {/* Variables list */}
      {vars.length === 0 ? (
        <Card className="bg-surface-2 flex flex-col items-center justify-center gap-3 p-12">
          <Key className="text-foreground/60 h-8 w-8" />
          <Text color="muted">No environment variables configured</Text>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => {
              setEditingKey('');
              setEditingValue('');
              setSaveError(null);
              setShowValue(false);
              setAddModalOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Add your first variable
          </Button>
        </Card>
      ) : (
        <Stack
          className="border-border divide-border divide-y rounded-lg border"
          gap="none"
        >
          {vars.map((envVar) => (
            <Inline key={envVar.id} justify="between" className="px-4 py-3">
              <Inline gap="sm">
                <Badge variant="outline" className="font-mono">
                  {envVar.key}
                </Badge>
                <Text color="muted" size="xs">
                  ••••••••
                </Text>
              </Inline>
              <Inline gap="xs">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleUpdate(envVar)}
                >
                  Update
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setDeletingVar(envVar);
                    setDeleteError(null);
                    setDeleteModalOpen(true);
                  }}
                >
                  <Trash2 className="text-destructive h-4 w-4" />
                </Button>
              </Inline>
            </Inline>
          ))}
        </Stack>
      )}

      {/* Add/Update Dialog */}
      <Dialog
        open={addModalOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setAddModalOpen(false);
            setEditingKey('');
            setEditingValue('');
            setSaveError(null);
            setShowValue(false);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingKey
                ? 'Update Environment Variable'
                : 'Add Environment Variable'}
            </DialogTitle>
            <DialogDescription>
              Environment variable values are encrypted at rest and never
              displayed after saving.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <Stack gap="md">
              {saveError && (
                <Alert variant="destructive">
                  <AlertDescription>{saveError}</AlertDescription>
                </Alert>
              )}
              <Stack gap="xs">
                <Label htmlFor="envKey">Key</Label>
                <Input
                  id="envKey"
                  type="text"
                  placeholder="DATABASE_URL"
                  value={editingKey}
                  onChange={(e) => setEditingKey(e.target.value.toUpperCase())}
                  className="mt-1 font-mono"
                  disabled={
                    vars.some((v) => v.key === editingKey) && !!editingKey
                  }
                />
                <Text color="muted" size="xs" className="mt-1">
                  Must start with a letter or underscore. Letters, numbers, and
                  underscores only.
                </Text>
              </Stack>
              <Stack gap="xs">
                <Label htmlFor="envValue">Value</Label>
                <Stack className="relative mt-1">
                  <Input
                    id="envValue"
                    type={showValue ? 'text' : 'password'}
                    placeholder="Enter value..."
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    className="pr-10 font-mono"
                  />
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => setShowValue(!showValue)}
                    className="absolute top-1/2 right-2 -translate-y-1/2"
                  >
                    {showValue ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAddModalOpen(false);
                setEditingKey('');
                setEditingValue('');
                setSaveError(null);
                setShowValue(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!editingKey.trim() || !editingValue.trim()}
              loading={saving}
            >
              {editingKey && vars.some((v) => v.key === editingKey)
                ? 'Update'
                : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteModalOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setDeleteModalOpen(false);
            setDeletingVar(null);
            setDeleteError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Environment Variable</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogBody>
            <Stack gap="md">
              {deleteError && (
                <Alert variant="destructive">
                  <AlertDescription>{deleteError}</AlertDescription>
                </Alert>
              )}
              {deletingVar && (
                <Card className="bg-surface-2 p-4">
                  <code className="font-mono text-sm">{deletingVar.key}</code>
                </Card>
              )}
              <Text color="muted" size="sm">
                This will permanently remove the variable. Any dev sessions
                using this variable will need to be restarted.
              </Text>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteModalOpen(false);
                setDeletingVar(null);
                setDeleteError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              loading={deleting}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
